import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TimeEntryFilters from './components/TimeEntryFilters';
import TimeEntryTable from './components/TimeEntryTable';
import CorrectionModal from './components/CorrectionModal';
import PendingCorrectionsPanel from './components/PendingCorrectionsPanel';
import AuditActivityFeed from './components/AuditActivityFeed';
import BulkCorrectionModal from './components/BulkCorrectionModal';
import ManualTimeEntryModal from './components/ManualTimeEntryModal';
import { useSupabase } from '../../hooks/useSupabase';
import { useColumnVisibility } from '../../contexts/ColumnVisibilityContext';

// Simple time/date helpers - no timezone conversion needed since DB stores local time
const extractTimeFromTimestamp = (timestamp) => {
  if (!timestamp) return '';
  // Extract HH:MM:SS from timestamp like "2025-09-25 16:50:10.456"
  const match = timestamp.match(/\d{2}:\d{2}:\d{2}/);
  return match ? match[0] : '';
};

const extractDateFromTimestamp = (timestamp) => {
  if (!timestamp) return '';
  // Extract YYYY-MM-DD from timestamp like "2025-09-25 16:50:10.456"
  const match = timestamp.match(/\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : '';
};

const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  return `${dateStr} ${normalizedTime}`;
};

// Helper function to format display time (cap at 17:00 for display)
const formatDisplayTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':').map(Number);
  const timeMinutes = hours * 60 + minutes;
  const maxDisplayMinutes = 17 * 60; // 17:00 = 1020 minutes
  
  if (timeMinutes > maxDisplayMinutes) {
    return '17:00:00'; // Show capped time for display
  }
  return timeString; // Show original time if within limits
};

// Helper function to calculate mini-job monthly distribution
const calculateMiniJobMonthlyDistribution = (entries, currentMonth) => {
  // Filter mini-job entries for the current month
  const miniJobEntries = entries.filter(entry => 
    entry.employee?.hire_type === 'Mini-job' && 
    entry.date && 
    entry.date.startsWith(currentMonth)
  );
  
  if (miniJobEntries.length === 0) return {};
  
  // Calculate total days and target hours (43 hours)
  const totalDays = miniJobEntries.length;
  const targetHours = 43;
  
  // Calculate how many 3-hour days we need
  const threeHourDays = targetHours - (totalDays * 2);
  const twoHourDays = totalDays - threeHourDays;
  
  // Create distribution array
  const distribution = [];
  for (let i = 0; i < threeHourDays; i++) {
    distribution.push(3);
  }
  for (let i = 0; i < twoHourDays; i++) {
    distribution.push(2);
  }
  
  // Shuffle the distribution to avoid patterns
  for (let i = distribution.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [distribution[i], distribution[j]] = [distribution[j], distribution[i]];
  }
  
  // Create a map of date to hours
  const dateToHours = {};
  miniJobEntries.forEach((entry, index) => {
    dateToHours[entry.date] = distribution[index] || 2;
  });
  
  return dateToHours;
};

// Helper function to calculate adjusted Gehen time for mini-job employees
const calculateAdjustedGehenTime = (clockIn, clockOut, hireType, date, monthlyDistribution) => {
  if (hireType !== 'Mini-job' || !clockIn || !clockOut) {
    return clockOut; // Return original time for non-mini-job employees
  }
  
  // Get the target hours for this date from monthly distribution
  const targetHours = monthlyDistribution[date] || 2;
  
  // Parse clock-in time
  const [inH, inM] = clockIn.split(':').map(Number);
  let clockInMinutes = inH * 60 + inM;
  
  // Apply minimum start time of 8:00 (480 minutes)
  const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
  if (clockInMinutes < minStartMinutes) {
    clockInMinutes = minStartMinutes;
  }
  
  // Calculate adjusted clock-out time (Kommen + target hours)
  const adjustedClockOutMinutes = clockInMinutes + (targetHours * 60);
  
  // Convert back to time string
  const adjustedHours = Math.floor(adjustedClockOutMinutes / 60);
  const adjustedMinutes = adjustedClockOutMinutes % 60;
  
  return `${String(adjustedHours).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')}:00`;
};

const TimeEntryCorrections = () => {
  const navigate = useNavigate();
  const { supabase, error } = useSupabase();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { isColumnsHidden, toggleColumnsVisibility } = useColumnVisibility();
  
  const [timeEntries, setTimeEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [auditActivities, setAuditActivities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('entries'); // 'entries', 'pending', 'audit'
  const [tableKey, setTableKey] = useState(0); // Key to force table re-render
  
  // Modal states
  const [correctionModal, setCorrectionModal] = useState({
    isOpen: false,
    entry: null,
    loading: false
  });
  
  const [bulkModal, setBulkModal] = useState({
    isOpen: false,
    selectedEntries: [],
    loading: false
  });

  const [manualEntryModal, setManualEntryModal] = useState({
    isOpen: false,
    loading: false
  });

  // Mock data
  const mockTimeEntries = [
    {
      id: 'te001',
      employee: {
        id: 'EMP001',
        name: 'John Smith',
        hireType: 'Vollzeit',
        tagesrate: 15.50
      },
      date: '2025-01-03',
      clockIn: '08:15:00',
      clockOut: '17:30:00',
      originalClockIn: '08:15:00',
      originalClockOut: '17:30:00',
      entryType: 'kiosk',
      status: 'normal',
      location: 'main-entrance',
      notes: '',
      dauer: 0,
      dauerS: 0,
      dauerT: 0
    },
    {
      id: 'te002',
      employee: {
        id: 'EMP002',
        name: 'Sarah Johnson',
        hireType: 'Teilzeit',
        tagesrate: 18.75
      },
      date: '2025-01-03',
      clockIn: '07:45:00',
      clockOut: '16:15:00',
      originalClockIn: '07:50:00',
      originalClockOut: '16:15:00',
      entryType: 'manual',
      status: 'corrected',
      location: 'warehouse-dock',
      notes: 'Corrected clock-in time due to system delay',
      dauerT: 0,
      dauerS: 0
    },
    {
      id: 'te003',
      employee: {
        id: 'EMP003',
        name: 'Michael Brown',
        hireType: 'Mini-job',
        tagesrate: 12.00
      },
      date: '2025-01-03',
      clockIn: '09:00:00',
      clockOut: '',
      originalClockIn: '09:00:00',
      originalClockOut: '',
      entryType: 'qr',
      status: 'flagged',
      location: 'office-lobby',
      notes: 'Missing clock-out entry',
      dauerT: 0,
      dauerS: 0
    },
    {
      id: 'te004',
      employee: {
        id: 'EMP004',
        name: 'Emily Davis',
        hireType: 'Vollzeit',
        tagesrate: 22.30
      },
      date: '2025-01-02',
      clockIn: '08:30:00',
      clockOut: '17:00:00',
      originalClockIn: '08:30:00',
      originalClockOut: '17:00:00',
      entryType: 'kiosk',
      status: 'pending',
      location: 'main-entrance',
      notes: '',
      dauer: 0,
      dauerS: 0,
      dauerT: 0
    },
    {
      id: 'te005',
      employee: {
        id: 'EMP005',
        name: 'David Wilson',
        hireType: 'Vollzeit',
        tagesrate: 25.00
      },
      date: '2025-01-02',
      clockIn: '06:00:00',
      clockOut: '14:30:00',
      originalClockIn: '06:00:00',
      originalClockOut: '14:30:00',
      entryType: 'mobile',
      status: 'approved',
      location: 'manufacturing-floor',
      notes: 'Early shift maintenance',
      dauerT: 0,
      dauerS: 0
    }
  ];

  const mockPendingCorrections = [
    {
      id: 'pc001',
      employee: {
        id: 'EMP006',
        name: 'Lisa Anderson',
        hireType: 'Mini-job',
        tagesrate: 11.50
      },
      date: '2025-01-03',
      originalClockIn: '09:15:00',
      originalClockOut: '18:00:00',
      newClockIn: '09:00:00',
      newClockOut: '18:00:00',
      reason: 'Employee was delayed due to traffic but arrived at 9:00 AM. Kiosk recorded 9:15 AM due to system lag.',
      category: 'system-error',
      priority: 'medium',
      requestedBy: 'Manager Smith',
      requestedAt: '2025-01-03T10:30:00Z',
      entryType: 'kiosk'
    },
    {
      id: 'pc002',
      employee: {
        id: 'EMP007',
        name: 'Robert Taylor',
        hireType: 'Vollzeit',
        tagesrate: 19.80
      },
      date: '2025-01-02',
      originalClockIn: '08:00:00',
      originalClockOut: '',
      newClockIn: '08:00:00',
      newClockOut: '16:30:00',
      reason: 'Employee forgot to clock out. Confirmed departure time with security logs.',
      category: 'employee-request',
      priority: 'high',
      requestedBy: 'HR Department',
      requestedAt: '2025-01-03T09:15:00Z',
      entryType: 'manual'
    }
  ];

  const mockAuditActivities = [
    {
      id: 'audit001',
      type: 'correction',
      user: { id: 'admin1', name: 'Admin User' },
      target: { employee: 'Sarah Johnson' },
      details: {
        changes: {
          clockIn: { from: '07:50:00', to: '07:45:00' }
        },
        reason: 'System delay correction'
      },
      timestamp: '2025-01-03T14:30:00Z',
      ipAddress: '192.168.1.100'
    },
    {
      id: 'audit002',
      type: 'approval',
      user: { id: 'manager1', name: 'Manager Smith' },
      target: { employee: 'Emily Davis' },
      details: {
        message: 'Approved overtime correction'
      },
      timestamp: '2025-01-03T13:45:00Z',
      ipAddress: '192.168.1.105'
    },
    {
      id: 'audit003',
      type: 'system',
      user: { id: 'system', name: 'System' },
      target: { employee: 'Multiple' },
      details: {
        message: 'Bulk DST adjustment applied to 127 entries'
      },
      timestamp: '2025-01-03T02:00:00Z',
      ipAddress: 'system'
    },
    {
      id: 'audit004',
      type: 'export',
      user: { id: 'hr1', name: 'HR Manager' },
      target: { employee: 'All' },
      details: {
        message: 'Exported time entries report for payroll processing'
      },
      timestamp: '2025-01-03T11:20:00Z',
      ipAddress: '192.168.1.110'
    }
  ];

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      try {
        // Load employees
        const { data: employeesData, error: employeesError } = await supabase
          .from('employees')
          .select('*')
          .eq('is_active', true)
          .order('name');
        
        if (employeesError) throw employeesError;
        setEmployees(employeesData || []);

        // Load time entries with employee data
        const { data: timeEntriesData, error: timeEntriesError } = await supabase
          .from('time_entries')
          .select(`
            *,
            employees (
              id,
              employee_code,
              name,
              hire_type,
              position,
              tagesrate
            )
          `)
          .order('clock_in_time', { ascending: false });
        
        if (timeEntriesError) throw timeEntriesError;
        
        // Helper function to calculate Dauer, Dauer S, and Dauer T
        const calculateTimeValues = (clockIn, clockOut, hireType, realClockOut = null) => {
          if (!clockIn || !clockOut) {
            return { dauer: 0, dauerS: 0, dauerT: 0 };
          }
          
          // Apply 08:00 minimum start time adjustment
          const [inH, inM] = clockIn.split(':').map(Number);
          const [outH, outM] = clockOut.split(':').map(Number);
          
          // Convert to minutes for easier calculation
          let clockInMinutes = inH * 60 + inM;
          const adjustedClockOutMinutes = outH * 60 + outM;
          
          // Apply minimum start time of 8:00 (480 minutes)
          const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
          if (clockInMinutes < minStartMinutes) {
            clockInMinutes = minStartMinutes;
          }
          
          // Calculate Dauer from adjusted time (Kommen to Gehen)
          const adjustedWorkingMinutes = Math.max(0, adjustedClockOutMinutes - clockInMinutes);
          const adjustedTotalHours = adjustedWorkingMinutes / 60;
          
          // Calculate Dauer T from real time (Kommen to Gehen S) for mini-job employees
          let realTotalHours = adjustedTotalHours;
          if (hireType === 'Mini-job' && realClockOut) {
            const [realOutH, realOutM] = realClockOut.split(':').map(Number);
            const realClockOutMinutes = realOutH * 60 + realOutM;
            const realWorkingMinutes = Math.max(0, realClockOutMinutes - clockInMinutes);
            realTotalHours = realWorkingMinutes / 60;
          }
          
          if (adjustedTotalHours <= 0) {
            return { dauer: 0, dauerS: 0, dauerT: 0 };
          }
          
          let dauer = 0;
          let dauerS = 0;
          
          if (hireType === 'Vollzeit') {
            // For Vollzeit: Dauer = first 8h, Dauer S = rest (8 hours work day)
            dauer = Math.min(adjustedTotalHours, 8);
            dauerS = Math.max(0, adjustedTotalHours - 8);
          } else if (hireType === 'Mini-job') {
            // For Mini-job: Dauer = adjusted time (2-3 hours), Dauer S = real time - Dauer
            dauer = adjustedTotalHours; // This will be 2 or 3 hours
            dauerS = Math.max(0, realTotalHours - dauer);
          } else {
            // For other types, use total hours as Dauer
            dauer = adjustedTotalHours;
            dauerS = 0;
          }
          
          return {
            dauer: dauer,
            dauerS: dauerS,
            dauerT: realTotalHours // Use real total hours for Dauer T
          };
        };
        
        // Calculate monthly distribution for mini-job employees
        const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM format
        const monthlyDistribution = calculateMiniJobMonthlyDistribution(
          timeEntriesData?.map(entry => ({
            employee: { hire_type: entry.employees?.hire_type },
            date: entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : ''
          })) || [], 
          currentMonth
        );

        // Transform time entries to match component structure
        const transformedEntries = (timeEntriesData || []).map(entry => {
          const hireType = entry.employees?.hire_type || 'Vollzeit';
          const clockIn = entry.clock_in_time ? extractTimeFromTimestamp(entry.clock_in_time) : '';
          const clockOut = entry.clock_out_time ? extractTimeFromTimestamp(entry.clock_out_time) : '';
          const entryDate = entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : '';
          
          // Calculate adjusted Gehen time for mini-job employees
          const adjustedClockOut = calculateAdjustedGehenTime(clockIn, clockOut, hireType, entryDate, monthlyDistribution);
          
          // Use adjusted time for Dauer calculations for mini-job employees
          const timeForCalculation = hireType === 'Mini-job' ? adjustedClockOut : clockOut;
          const realTimeForCalculation = hireType === 'Mini-job' ? clockOut : null;
          const timeValues = calculateTimeValues(clockIn, timeForCalculation, hireType, realTimeForCalculation);
          
          // Debug logging for mini-job employees to see what's happening
          if (hireType === 'Mini-job') {
            console.log('=== MINI-JOB DEBUG ===');
            console.log('Employee:', entry.employees?.name);
            console.log('Date:', entryDate);
            console.log('Original clockIn:', clockIn);
            console.log('Original clockOut (Real):', clockOut);
            console.log('Adjusted clockOut (Gehen):', adjustedClockOut);
            console.log('Monthly distribution for this date:', monthlyDistribution[entryDate]);
            console.log('Calculated timeValues:', timeValues);
            console.log('========================');
          }
          
          return {
            id: entry.id,
            employee: {
              id: entry.employees?.id || entry.employee_id,
              name: entry.employees?.name || 'Unknown Employee',
              employee_code: entry.employees?.employee_code,
              hire_type: hireType,
              tagesrate: entry.employees?.tagesrate || 0
            },
            date: entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : (entry.clock_out_time ? extractDateFromTimestamp(entry.clock_out_time) : ''),
            clockIn: clockIn,
            clockOut: adjustedClockOut, // Show adjusted time for mini-job, real time for others
            clockOutReal: clockOut, // Always store real time for Gehen S column
            originalClockIn: clockIn,
            originalClockOut: clockOut, // Keep real time for calculations
            entryType: entry.entry_type || 'regular',
            status: 'normal', // Default status, can be enhanced based on business logic
            location: 'main-entrance', // Default location
            notes: entry.notes || '',
            totalHours: entry.total_hours,
            breakDuration: entry.break_duration || 0,
            dauer: timeValues.dauer,
            dauerS: timeValues.dauerS,
            dauerT: timeValues.dauerT
          };
        });
        
        setTimeEntries(transformedEntries);
        setFilteredEntries(transformedEntries);
        
        // Update database with correct dauer values if they differ from calculated values
        const entriesToUpdate = [];
        for (let i = 0; i < timeEntriesData.length; i++) {
          const entry = timeEntriesData[i];
          const transformedEntry = transformedEntries[i];
          
          // Check if database values differ from calculated values
          const dbDauer = entry.dauer || 0;
          const dbDauerS = entry.dauer_s || 0;
          const dbDauerT = entry.dauer_t || 0;
          
          const calcDauer = transformedEntry.dauer;
          const calcDauerS = transformedEntry.dauerS;
          const calcDauerT = transformedEntry.dauerT;
          
          // If values differ significantly (more than 0.01 hours), mark for update
          const dauerDiff = Math.abs(dbDauer - calcDauer);
          const dauerSDiff = Math.abs(dbDauerS - calcDauerS);
          const dauerTDiff = Math.abs(dbDauerT - calcDauerT);
          
          if (dauerDiff > 0.01 || dauerSDiff > 0.01 || dauerTDiff > 0.01) {
            entriesToUpdate.push({
              id: entry.id,
              dauer: calcDauer,
              dauer_s: calcDauerS,
              dauer_t: calcDauerT
            });
          }
        }
        
        // Update database with correct values
        if (entriesToUpdate.length > 0) {
          console.log(`Updating ${entriesToUpdate.length} entries with correct dauer values`);
          for (const updateEntry of entriesToUpdate) {
            try {
              const { error } = await supabase
                .from('time_entries')
                .update({
                  dauer: updateEntry.dauer,
                  dauer_s: updateEntry.dauer_s,
                  dauer_t: updateEntry.dauer_t,
                  updated_at: new Date().toISOString()
                })
                .eq('id', updateEntry.id);
              
              if (error) {
                console.error('Error updating dauer values for entry', updateEntry.id, error);
              }
            } catch (error) {
              console.error('Error updating dauer values for entry', updateEntry.id, error);
            }
          }
        }

        // Load pending corrections (with fallback if table doesn't exist)
        let correctionsData = [];
        try {
          const { data, error: correctionsError } = await supabase
            .from('time_corrections')
            .select(`
              *,
              time_entries (
                id,
                employee_id,
                employees (
                  id,
                  employee_code,
                  name,
                  hire_type
                )
              )
            `)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });
          
          if (correctionsError) {
            console.warn('Time corrections table not found, using empty array:', correctionsError);
            correctionsData = [];
          } else {
            correctionsData = data || [];
          }
        } catch (error) {
          console.warn('Error loading time corrections, using empty array:', error);
          correctionsData = [];
        }
        
        // Transform corrections to match component structure
        const transformedCorrections = (correctionsData || []).map(correction => ({
          id: correction.id,
          employee: {
            id: correction.time_entries?.employees?.employee_code || correction.time_entries?.employee_id || correction.requested_by,
            name: correction.time_entries?.employees?.name || 'Unknown Employee',
            hireType: correction.time_entries?.employees?.hire_type || 'Vollzeit'
          },
          date: correction.original_clock_in ? extractDateFromTimestamp(correction.original_clock_in) : (correction.corrected_clock_in ? extractDateFromTimestamp(correction.corrected_clock_in) : ''),
          originalClockIn: correction.original_clock_in ? extractTimeFromTimestamp(correction.original_clock_in) : '',
          originalClockOut: correction.original_clock_out ? extractTimeFromTimestamp(correction.original_clock_out) : '',
          newClockIn: correction.corrected_clock_in ? extractTimeFromTimestamp(correction.corrected_clock_in) : '',
          newClockOut: correction.corrected_clock_out ? extractTimeFromTimestamp(correction.corrected_clock_out) : '',
          reason: correction.reason,
          category: 'employee-request', // Default category
          priority: 'medium', // Default priority
          requestedBy: 'Employee', // Can be enhanced with user lookup
          requestedAt: correction.created_at,
          entryType: 'regular'
        }));
        
        setPendingCorrections(transformedCorrections);

        // Load audit activities (with fallback if table doesn't exist)
        let auditData = [];
        try {
          const { data, error: auditError } = await supabase
            .from('audit_logs')
            .select('*')
            .eq('table_name', 'time_entries')
            .order('changed_at', { ascending: false })
            .limit(50);
          
          if (auditError) {
            console.warn('Audit logs table not found, using empty array:', auditError);
            auditData = [];
          } else {
            auditData = data || [];
          }
        } catch (error) {
          console.warn('Error loading audit logs, using empty array:', error);
          auditData = [];
        }
        
        // Transform audit logs to match component structure
        const transformedAudit = (auditData || []).map(audit => ({
          id: audit.id,
          type: audit.action,
          user: { id: audit.changed_by, name: 'System User' }, // Can be enhanced with user lookup
          target: { employee: 'Time Entry' },
          details: {
            changes: audit.old_values || {},
            newValues: audit.new_values || {},
            message: `${audit.action} performed on time entry`
          },
          timestamp: audit.changed_at,
          ipAddress: '192.168.1.100' // Default IP, can be enhanced
        }));
        
        setAuditActivities(transformedAudit);
        
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback to empty arrays on error
        setTimeEntries([]);
        setFilteredEntries([]);
        setPendingCorrections([]);
        setAuditActivities([]);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    if (supabase) {
      loadData();
    }
  }, [supabase]);


  // Handle filters change
  const handleFiltersChange = (filters) => {
    let filtered = [...timeEntries];

    // Apply search query
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(entry => 
        entry?.employee?.name?.toLowerCase()?.includes(query) ||
        entry?.employee?.id?.toLowerCase()?.includes(query) ||
        entry?.employee?.employee_code?.toLowerCase()?.includes(query) ||
        entry?.notes?.toLowerCase()?.includes(query)
      );
    }

    // Apply date range
    if (filters?.dateRange?.startDate) {
      filtered = filtered?.filter(entry => entry?.date >= filters?.dateRange?.startDate);
    }
    if (filters?.dateRange?.endDate) {
      filtered = filtered?.filter(entry => entry?.date <= filters?.dateRange?.endDate);
    }

    // Apply other filters
    if (filters?.employee) {
      filtered = filtered?.filter(entry => entry?.employee?.id === filters?.employee);
    }
    if (filters?.hireType) {
      filtered = filtered?.filter(entry => entry?.employee?.hire_type === filters?.hireType);
    }
    if (filters?.entryType) {
      filtered = filtered?.filter(entry => entry?.entryType === filters?.entryType);
    }
    if (filters?.status) {
      filtered = filtered?.filter(entry => entry?.status === filters?.status);
    }

    setFilteredEntries(filtered);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilteredEntries(timeEntries);
  };

  // Function to load time entries (can be called after corrections)
  const loadTimeEntries = async () => {
    try {
      console.log('Loading time entries...');
      // Load time entries with employee data
      const { data: timeEntriesData, error: timeEntriesError } = await supabase
        .from('time_entries')
        .select(`
          *,
          employees (
            id,
            employee_code,
            name,
            hire_type,
            position,
            tagesrate
          )
        `)
        .order('clock_in_time', { ascending: false });
      
      if (timeEntriesError) {
        console.error('Error loading time entries:', timeEntriesError);
        throw timeEntriesError;
      }
      
      console.log('Time entries loaded:', timeEntriesData?.length || 0, 'entries');
      
      // Helper function to calculate Dauer, Dauer S, and Dauer T
      const calculateTimeValues = (clockIn, clockOut, hireType, realClockOut = null) => {
        if (!clockIn || !clockOut) {
          return { dauer: 0, dauerS: 0, dauerT: 0 };
        }
        
        // Apply 08:00 minimum start time adjustment
        const [inH, inM] = clockIn.split(':').map(Number);
        const [outH, outM] = clockOut.split(':').map(Number);
        
        // Convert to minutes for easier calculation
        let clockInMinutes = inH * 60 + inM;
        const adjustedClockOutMinutes = outH * 60 + outM;
        
        // Apply minimum start time of 8:00 (480 minutes)
        const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
        if (clockInMinutes < minStartMinutes) {
          clockInMinutes = minStartMinutes;
        }
        
        // Calculate Dauer from adjusted time (Kommen to Gehen)
        const adjustedWorkingMinutes = Math.max(0, adjustedClockOutMinutes - clockInMinutes);
        const adjustedTotalHours = adjustedWorkingMinutes / 60;
        
        // Calculate Dauer T from real time (Kommen to Gehen S) for mini-job employees
        let realTotalHours = adjustedTotalHours;
        if (hireType === 'Mini-job' && realClockOut) {
          const [realOutH, realOutM] = realClockOut.split(':').map(Number);
          const realClockOutMinutes = realOutH * 60 + realOutM;
          const realWorkingMinutes = Math.max(0, realClockOutMinutes - clockInMinutes);
          realTotalHours = realWorkingMinutes / 60;
        }
        
        if (adjustedTotalHours <= 0) {
          return { dauer: 0, dauerS: 0, dauerT: 0 };
        }
        
        let dauer = 0;
        let dauerS = 0;
        
        if (hireType === 'Vollzeit') {
          // For Vollzeit: Dauer = first 8h, Dauer S = rest (8 hours work day)
          dauer = Math.min(adjustedTotalHours, 8);
          dauerS = Math.max(0, adjustedTotalHours - 8);
        } else if (hireType === 'Mini-job') {
          // For Mini-job: Dauer = adjusted time (2-3 hours), Dauer S = real time - Dauer
          dauer = adjustedTotalHours; // This will be 2 or 3 hours
          dauerS = Math.max(0, realTotalHours - dauer);
        } else {
          // For other types, use total hours as Dauer
          dauer = adjustedTotalHours;
          dauerS = 0;
        }
        
        return {
          dauer: dauer,
          dauerS: dauerS,
          dauerT: realTotalHours // Use real total hours for Dauer T
        };
      };
      
      // Calculate monthly distribution for mini-job employees
      const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM format
      const monthlyDistribution = calculateMiniJobMonthlyDistribution(
        timeEntriesData?.map(entry => ({
          employee: { hire_type: entry.employees?.hire_type },
          date: entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : ''
        })) || [], 
        currentMonth
      );

      // Transform time entries to match component structure
      const transformedEntries = (timeEntriesData || []).map(entry => {
        const hireType = entry.employees?.hire_type || 'Vollzeit';
        const clockIn = entry.clock_in_time ? extractTimeFromTimestamp(entry.clock_in_time) : '';
        const clockOut = entry.clock_out_time ? extractTimeFromTimestamp(entry.clock_out_time) : '';
        const entryDate = entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : '';
        
        // Calculate adjusted Gehen time for mini-job employees
        const adjustedClockOut = calculateAdjustedGehenTime(clockIn, clockOut, hireType, entryDate, monthlyDistribution);
        
        // Use adjusted time for Dauer calculations for mini-job employees
        const timeForCalculation = hireType === 'Mini-job' ? adjustedClockOut : clockOut;
        const realTimeForCalculation = hireType === 'Mini-job' ? clockOut : null;
        const timeValues = calculateTimeValues(clockIn, timeForCalculation, hireType, realTimeForCalculation);
        
        return {
          id: entry.id,
          employee: {
            id: entry.employees?.id || entry.employee_id,
            name: entry.employees?.name || 'Unknown Employee',
            employee_code: entry.employees?.employee_code,
            hire_type: hireType,
            tagesrate: entry.employees?.tagesrate || 0
          },
          date: entry.clock_in_time ? extractDateFromTimestamp(entry.clock_in_time) : (entry.clock_out_time ? extractDateFromTimestamp(entry.clock_out_time) : ''),
          clockIn: clockIn,
          clockOut: adjustedClockOut, // Show adjusted time for mini-job, real time for others
          clockOutReal: clockOut, // Always store real time for Gehen S column
          originalClockIn: clockIn,
          originalClockOut: clockOut,
          entryType: entry.entry_type || 'regular',
          status: 'normal',
          location: 'main-entrance',
          notes: entry.notes || '',
          totalHours: entry.total_hours,
          breakDuration: entry.break_duration || 0,
          dauer: timeValues.dauer,
          dauerS: timeValues.dauerS,
          dauerT: timeValues.dauerT
        };
      });
      
      setTimeEntries(transformedEntries);
      setFilteredEntries(transformedEntries);
      
      console.log('Time entries refreshed successfully:', transformedEntries.length, 'entries');
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  };

  // Handle refresh table data
  const handleRefreshTable = async () => {
    setLoading(true);
    try {
      await loadTimeEntries();
      
      // Increment table key to force re-render
      setTableKey(prev => prev + 1);
      
      console.log('Table refreshed successfully');
    } catch (error) {
      console.error('Error refreshing table:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit entry
  const handleEditEntry = (entry) => {
    setCorrectionModal({
      isOpen: true,
      entry,
      loading: false
    });
  };

  // Handle save correction
  const handleSaveCorrection = async (correctionData) => {
    setCorrectionModal(prev => ({ ...prev, loading: true }));

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Calculate mini-job duration values if applicable
      let dauerTValue = correctionData.dauerT;
      let dauerSValue = correctionData.dauerS;
      
      if (correctionData.employee?.hireType === 'Mini-job' && correctionData.clockIn && correctionData.clockOut) {
        const start = new Date(`2000-01-01T${correctionData.clockIn}`);
        const end = new Date(`2000-01-01T${correctionData.clockOut}`);
        const diff = end - start;
        
        if (diff > 0) {
          const totalHours = diff / (1000 * 60 * 60);
          dauerTValue = parseFloat(totalHours.toFixed(2)); // Total hours
          dauerSValue = parseFloat(Math.max(0, totalHours - 2).toFixed(2)); // Remaining hours after first 2
        }
      }
      
      // Update the time entry in the database
      const updateData = {
        clock_in_time: combineDateTime(correctionData.date, correctionData.clockIn),
        clock_out_time: combineDateTime(correctionData.date, correctionData.clockOut),
        notes: correctionData.notes || null,
        dauer_t: dauerTValue,
        dauer_s: dauerSValue,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('time_entries')
        .update(updateData)
        .eq('id', correctionData.id);

      if (updateError) throw updateError;

      // Create audit log entry (with fallback if table doesn't exist)
      try {
        const auditData = {
          table_name: 'time_entries',
          record_id: correctionData.id,
          action: 'update',
          old_values: {
            clock_in_time: combineDateTime(correctionData.date, correctionData.originalClockIn),
            clock_out_time: combineDateTime(correctionData.date, correctionData.originalClockOut),
            notes: correctionData.notes || null
          },
          new_values: updateData,
          changed_by: user?.id,
          changed_at: new Date().toISOString()
        };

        const { error: auditError } = await supabase
          .from('audit_logs')
          .insert(auditData);

        if (auditError) {
          console.warn('Failed to create audit log:', auditError);
        }
      } catch (auditError) {
        console.warn('Audit logs table not available:', auditError);
      }

      // Close modal immediately after successful database update
      setCorrectionModal({ isOpen: false, entry: null, loading: false });

      // Add audit activity to local state
      const newActivity = {
        id: `audit${Date.now()}`,
        type: 'correction',
        user: { id: user?.id, name: 'Current User' },
        target: { employee: correctionData?.employee?.name },
        details: {
          changes: {
            clockIn: correctionData?.originalClockIn !== correctionData?.clockIn ? 
              { from: correctionData?.originalClockIn, to: correctionData?.clockIn } : null,
            clockOut: correctionData?.originalClockOut !== correctionData?.clockOut ? 
              { from: correctionData?.originalClockOut, to: correctionData?.clockOut } : null
          },
          reason: correctionData?.correctionReason
        },
        timestamp: new Date()?.toISOString(),
        ipAddress: '192.168.1.100'
      };
      setAuditActivities(prev => [newActivity, ...prev]);

      // Refresh data in background after modal is closed
      setTimeout(async () => {
        try {
          // Refresh data from database to ensure consistency and complete re-render
          await loadTimeEntries();
          // Increment table key to force complete re-render
          setTableKey(prev => prev + 1);
        } catch (refreshError) {
          console.error('Error refreshing data after correction:', refreshError);
        }
      }, 100);
    } catch (error) {
      console.error('Failed to save correction:', error);
      setCorrectionModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle individual time entry deletion
  const handleDeleteEntry = async (entry) => {
    if (window.confirm(`Sind Sie sicher, dass Sie den Zeiteintrag vom ${entry?.date} für ${entry?.employee?.name} löschen möchten?`)) {
      try {
        const { error } = await supabase
          .from('time_entries')
          .delete()
          .eq('id', entry?.id);

        if (error) {
          throw error;
        }

        // Update local state
        const updatedEntries = timeEntries?.filter(e => e?.id !== entry?.id);
        setTimeEntries(updatedEntries);
        setFilteredEntries(updatedEntries);
        
        // Increment table key to force re-render
        setTableKey(prev => prev + 1);
        
        console.log('Time entry deleted successfully');
      } catch (error) {
        console.error('Failed to delete time entry:', error);
        alert('Fehler beim Löschen des Zeiteintrags: ' + error.message);
      }
    }
  };

  // Handle bulk actions
  const handleBulkAction = (action, entryIds) => {
    switch (action) {
      case 'approve':
        // Update entries status to approved
        const approvedEntries = timeEntries?.map(entry =>
          entryIds?.includes(entry?.id) ? { ...entry, status: 'approved' } : entry
        );
        setTimeEntries(approvedEntries);
        setFilteredEntries(approvedEntries);
        break;
        
      case 'flag':
        // Update entries status to flagged
        const flaggedEntries = timeEntries?.map(entry =>
          entryIds?.includes(entry?.id) ? { ...entry, status: 'flagged' } : entry
        );
        setTimeEntries(flaggedEntries);
        setFilteredEntries(flaggedEntries);
        break;
        
      case 'bulk-correct':
        setBulkModal({
          isOpen: true,
          selectedEntries: entryIds,
          loading: false
        });
        break;

      case 'delete':
        // Handle bulk deletion
        if (window.confirm(`Sind Sie sicher, dass Sie ${entryIds?.length} Zeiteinträge löschen möchten?`)) {
          handleBulkDelete(entryIds);
        }
        break;
        
      case 'view-audit': navigate('/audit-trail-review', { state: { entryId: entryIds?.[0] } });
        break;
        
      default:
        console.log(`Bulk action: ${action}`, entryIds);
    }
  };

  // Handle bulk deletion
  const handleBulkDelete = async (entryIds) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .in('id', entryIds);

      if (error) {
        throw error;
      }

      // Update local state
      const updatedEntries = timeEntries?.filter(entry => !entryIds?.includes(entry?.id));
      setTimeEntries(updatedEntries);
      setFilteredEntries(updatedEntries);
      
      // Increment table key to force re-render
      setTableKey(prev => prev + 1);
      
      console.log(`${entryIds?.length} time entries deleted successfully`);
    } catch (error) {
      console.error('Failed to delete time entries:', error);
      alert('Fehler beim Löschen der Zeiteinträge: ' + error.message);
    }
  };

  // Handle manual time entry creation
  const handleManualEntryCreate = async (timeEntryData) => {
    setManualEntryModal(prev => ({ ...prev, loading: true }));
    
    try {
      // Combine date and time into proper timestamps
      const clockInTimestamp = `${timeEntryData.date} ${timeEntryData.clockIn}:00`;
      const clockOutTimestamp = `${timeEntryData.date} ${timeEntryData.clockOut}:00`;
      
      // Calculate working hours with time constraints (min 8:00, max 17:00)
      const [inH, inM] = timeEntryData.clockIn.split(':').map(Number);
      const [outH, outM] = timeEntryData.clockOut.split(':').map(Number);
      
      // Convert to minutes for easier calculation
      let clockInMinutes = inH * 60 + inM;
      let clockOutMinutes = outH * 60 + outM;
      
      // Apply minimum start time of 8:00 (480 minutes)
      const minStartMinutes = 8 * 60; // 8:00 = 480 minutes
      if (clockInMinutes < minStartMinutes) {
        clockInMinutes = minStartMinutes;
      }
      
      // Apply maximum end time of 17:00 (1020 minutes)
      const maxEndMinutes = 17 * 60; // 17:00 = 1020 minutes
      if (clockOutMinutes > maxEndMinutes) {
        clockOutMinutes = maxEndMinutes;
      }
      
      // Calculate working time in minutes and convert to hours
      const workingMinutes = Math.max(0, clockOutMinutes - clockInMinutes);
      const totalHours = workingMinutes / 60;
      
      // Calculate Dauer, Dauer S, and Dauer T based on hire type
      let dauer = 0;
      let dauerS = 0;
      const dauerT = totalHours;
      
      if (timeEntryData.employee.hire_type === 'Vollzeit') {
        dauer = Math.min(totalHours, 8); // 8 hours work day
        dauerS = Math.max(0, totalHours - 8);
      } else if (timeEntryData.employee.hire_type === 'Mini-job') {
        dauer = Math.min(totalHours, 2);
        dauerS = Math.max(0, totalHours - 2);
      } else {
        dauer = totalHours;
        dauerS = 0;
      }

      // Check if employee already has a clock-in entry for this date
      const entryDate = timeEntryData.date;
      const { data: existingEntries, error: existingError } = await supabase
        .from('time_entries')
        .select('id, clock_in_time, clock_out_time')
        .eq('employee_id', timeEntryData.employee_id)
        .gte('clock_in_time', `${entryDate} 00:00:00`)
        .lt('clock_in_time', `${entryDate} 23:59:59`);

      if (existingError) {
        throw existingError;
      }

      // Check if there's already ANY clock-in entry for this date (regardless of clock-out status)
      if (existingEntries && existingEntries.length > 0) {
        throw new Error('Mitarbeiter hat sich bereits für diesen Tag eingestempelt. Nur ein Einstempeln pro Tag erlaubt.');
      }

      // Calculate Gehalt S (with dynamic pause deduction, max 1 hour)
      const tagesrate = timeEntryData.employee.tagesrate || 0;
      // Calculate dynamic pause: 15 minutes for every 2 hours worked (max 1 hour)
      const pauseBlocks = Math.floor(dauerT / 2);
      const calculatedPause = pauseBlocks * 0.25; // 15 minutes = 0.25 hours
      const pauseHours = Math.min(calculatedPause, 1); // Cap at 1 hour
      const effectiveHours = Math.max(0, dauerT - pauseHours); // Deduct dynamic pause
      const gehaltS = (effectiveHours / 8) * tagesrate;

      const { data, error } = await supabase
        .from('time_entries')
        .insert([{
          employee_id: timeEntryData.employee_id,
          clock_in_time: clockInTimestamp,
          clock_out_time: clockOutTimestamp,
          total_hours: totalHours,
          dauer: dauer,
          dauer_s: dauerS,
          dauer_t: dauerT,
          gehalt_s: gehaltS,
          notes: timeEntryData.notes || null,
          entry_type: 'regular',
          created_at: new Date().toISOString()
        }])
        .select(`
          *,
          employees!inner(*)
        `)
        .single();

      if (error) {
        throw error;
      }

      // Transform the new entry to match the expected format
      const newEntry = {
        id: data.id,
        employee: {
          id: data.employees.id,
          name: data.employees.name,
          employee_code: data.employees.employee_code,
          hire_type: data.employees.hire_type,
          tagesrate: data.employees.tagesrate
        },
        date: timeEntryData.date,
        clockIn: timeEntryData.clockIn,
        clockOut: timeEntryData.clockOut,
        totalHours: totalHours,
        dauer: dauer,
        dauerS: dauerS,
        dauerT: dauerT,
        gehaltS: gehaltS,
        notes: timeEntryData.notes || '',
        status: 'normal',
        entryType: 'regular',
        createdAt: data.created_at
      };

      // Add to local state
      setTimeEntries(prev => [newEntry, ...prev]);
      setFilteredEntries(prev => [newEntry, ...prev]);
      
      // Increment table key to force re-render
      setTableKey(prev => prev + 1);
      
      setManualEntryModal({ isOpen: false, loading: false });
      console.log('Manual time entry created successfully');
    } catch (error) {
      console.error('Failed to create manual time entry:', error);
      alert('Fehler beim Erstellen des Zeiteintrags: ' + error.message);
      setManualEntryModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle bulk correction
  const handleBulkCorrection = async (correctionData) => {
    setBulkModal(prev => ({ ...prev, loading: true }));

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Apply bulk correction logic based on type
      let updatedEntries = [...timeEntries];
      
      switch (correctionData?.type) {
        case 'approve-all':
          updatedEntries = updatedEntries?.map(entry =>
            correctionData?.entryIds?.includes(entry?.id) ? 
              { ...entry, status: 'approved' } : entry
          );
          break;
          
        case 'flag-all':
          updatedEntries = updatedEntries?.map(entry =>
            correctionData?.entryIds?.includes(entry?.id) ? 
              { ...entry, status: 'flagged' } : entry
          );
          break;
          
        case 'time-shift':
          const minutes = parseInt(correctionData?.value);
          updatedEntries = updatedEntries?.map(entry => {
            if (correctionData?.entryIds?.includes(entry?.id)) {
              const adjustTime = (timeStr) => {
                if (!timeStr) return timeStr;
                const [hours, mins] = timeStr?.split(':')?.map(Number);
                const totalMinutes = hours * 60 + mins + minutes;
                const newHours = Math.floor(totalMinutes / 60) % 24;
                const newMins = totalMinutes % 60;
                return `${String(newHours)?.padStart(2, '0')}:${String(newMins)?.padStart(2, '0')}:00`;
              };
              
              return {
                ...entry,
                clockIn: adjustTime(entry?.clockIn),
                clockOut: adjustTime(entry?.clockOut),
                status: 'corrected'
              };
            }
            return entry;
          });
          break;
          
        default:
          console.log('Bulk correction type not implemented:', correctionData?.type);
      }

      // Increment table key to force complete re-render
      setTableKey(prev => prev + 1);

      // Small delay to ensure database update is fully processed
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh data from database to ensure consistency and complete re-render
      await loadTimeEntries();

      // Add audit activity
      const newActivity = {
        id: `audit${Date.now()}`,
        type: 'correction',
        user: { id: 'current-user', name: 'Current User' },
        target: { employee: 'Multiple' },
        details: {
          message: `Bulk ${correctionData?.type} applied to ${correctionData?.entryIds?.length} entries`,
          reason: correctionData?.reason
        },
        timestamp: new Date()?.toISOString(),
        ipAddress: '192.168.1.100'
      };
      setAuditActivities(prev => [newActivity, ...prev]);

      setBulkModal({ isOpen: false, selectedEntries: [], loading: false });
    } catch (error) {
      console.error('Failed to apply bulk correction:', error);
      setBulkModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle pending correction actions
  const handleApprovePending = async (correctionId) => {
    const correction = pendingCorrections?.find(c => c?.id === correctionId);
    if (correction) {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        // Update correction status to approved (with fallback if table doesn't exist)
        try {
          const { error: updateError } = await supabase
            .from('time_corrections')
            .update({ 
              status: 'approved',
              approved_by: user?.id,
              updated_at: new Date().toISOString()
            })
            .eq('id', correctionId);

          if (updateError) {
            console.warn('Failed to update time correction:', updateError);
            return; // Exit early if update fails
          }
        } catch (error) {
          console.warn('Time corrections table not available:', error);
          return; // Exit early if table doesn't exist
        }

        // Remove from pending
        setPendingCorrections(prev => prev?.filter(c => c?.id !== correctionId));
        
        // Add audit activity
        const newActivity = {
          id: `audit${Date.now()}`,
          type: 'approval',
          user: { id: user?.id, name: 'Current User' },
          target: { employee: correction?.employee?.name },
          details: {
            message: 'Approved pending correction'
          },
          timestamp: new Date()?.toISOString(),
          ipAddress: '192.168.1.100'
        };
        setAuditActivities(prev => [newActivity, ...prev]);
      } catch (error) {
        console.error('Failed to approve correction:', error);
      }
    }
  };

  const handleRejectPending = async (correctionId) => {
    const correction = pendingCorrections?.find(c => c?.id === correctionId);
    if (correction) {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        // Update correction status to rejected (with fallback if table doesn't exist)
        try {
          const { error: updateError } = await supabase
            .from('time_corrections')
            .update({ 
              status: 'rejected',
              approved_by: user?.id,
              updated_at: new Date().toISOString()
            })
            .eq('id', correctionId);

          if (updateError) {
            console.warn('Failed to update time correction:', updateError);
            return; // Exit early if update fails
          }
        } catch (error) {
          console.warn('Time corrections table not available:', error);
          return; // Exit early if table doesn't exist
        }

        // Remove from pending
        setPendingCorrections(prev => prev?.filter(c => c?.id !== correctionId));
        
        // Add audit activity
        const newActivity = {
          id: `audit${Date.now()}`,
          type: 'rejection',
          user: { id: user?.id, name: 'Current User' },
          target: { employee: correction?.employee?.name },
          details: {
            message: 'Rejected pending correction'
          },
          timestamp: new Date()?.toISOString(),
          ipAddress: '192.168.1.100'
        };
        setAuditActivities(prev => [newActivity, ...prev]);
      } catch (error) {
        console.error('Failed to reject correction:', error);
      }
    }
  };

  const handleViewPendingDetails = (correction) => {
    setCorrectionModal({
      isOpen: true,
      entry: {
        ...correction,
        employee: correction?.employee,
        date: correction?.date,
        clockIn: correction?.originalClockIn,
        clockOut: correction?.originalClockOut
      },
      loading: false
    });
  };

  // Handle export
  const handleExport = () => {
    // Add audit activity
    const newActivity = {
      id: `audit${Date.now()}`,
      type: 'export',
      user: { id: 'current-user', name: 'Current User' },
      target: { employee: 'All' },
      details: {
        message: `Exported ${filteredEntries?.length} time entries`
      },
      timestamp: new Date()?.toISOString(),
      ipAddress: '192.168.1.100'
    };
    setAuditActivities(prev => [newActivity, ...prev]);

    // Simulate export
    console.log('Exporting time entries:', filteredEntries);
  };

  // Notification counts for header
  const notificationCounts = {
    orders: 0,
    technicians: 0
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-text-primary">Zeiteintragskorrekturen</h1>
              <p className="text-text-secondary mt-2">
                Überprüfen und bearbeiten Sie Mitarbeiterzeitaufzeichnungen mit vollständiger Audit-Trail-Dokumentation
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleRefreshTable}
                iconName="RefreshCw"
                iconPosition="left"
                className="touch-feedback"
                disabled={loading}
              >
                {loading ? 'Aktualisiere...' : 'Tabelle aktualisieren'}
              </Button>
              {/* <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
                className="touch-feedback"
              >
                Bericht exportieren
              </Button> */}
              <Button
                variant="outline"
                onClick={() => setManualEntryModal({ isOpen: true, loading: false })}
                iconName="Plus"
                iconPosition="left"
                className="touch-feedback"
              >
                Zeiteintrag erstellen
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/employee-management')}
                iconName="Users"
                iconPosition="left"
                className="touch-feedback"
              >
                Mitarbeiter verwalten
              </Button>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-1 mb-6 bg-muted rounded-lg p-1">
            <button
              onClick={() => setActiveView('entries')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'entries' ?'bg-background text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>Zeiteinträge ({filteredEntries?.length})</span>
              </div>
            </button>
            {/* <button
              onClick={() => setActiveView('pending')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'pending' ?'bg-background text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} />
                <span>Ausstehend ({pendingCorrections?.length})</span>
              </div>
            </button> */}
            {/* <button
              onClick={() => setActiveView('audit')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'audit' ?'bg-background text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Activity" size={16} />
                <span>Audit Trail</span>
              </div>
            </button> */}
          </div>

          {/* Content based on active view */}
          {activeView === 'entries' && (
            <div className="space-y-6">
              <TimeEntryFilters
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
                totalEntries={timeEntries?.length}
                filteredEntries={filteredEntries?.length}
                employees={employees}
              />
              
              {/* <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary">
                  Zeige {filteredEntries?.length} von {timeEntries?.length} Einträgen
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('bulk-correct', [])}
                  iconName="Edit3"
                  iconPosition="left"
                  className="touch-feedback"
                >
                  Massenkorrekturen
                </Button>
              </div> */}
              
              <TimeEntryTable
                key={`time-entry-table-${tableKey}`}
                entries={filteredEntries}
                onEditEntry={handleEditEntry}
                onDeleteEntry={handleDeleteEntry}
                onBulkAction={handleBulkAction}
                loading={loading}
                isColumnsHidden={isColumnsHidden}
              />
            </div>
          )}

          {activeView === 'pending' && (
            <PendingCorrectionsPanel
              pendingCorrections={pendingCorrections}
              onApprove={handleApprovePending}
              onReject={handleRejectPending}
              onViewDetails={handleViewPendingDetails}
            />
          )}

          {activeView === 'audit' && (
            <AuditActivityFeed
              activities={auditActivities}
              onLoadMore={() => console.log('Load more activities')}
              hasMore={false}
            />
          )}

            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <CorrectionModal
        isOpen={correctionModal?.isOpen}
        onClose={() => setCorrectionModal({ isOpen: false, entry: null, loading: false })}
        entry={correctionModal?.entry}
        onSave={handleSaveCorrection}
        loading={correctionModal?.loading}
      />
      <BulkCorrectionModal
        isOpen={bulkModal?.isOpen}
        onClose={() => setBulkModal({ isOpen: false, selectedEntries: [], loading: false })}
        selectedEntries={bulkModal?.selectedEntries}
        onApply={handleBulkCorrection}
        loading={bulkModal?.loading}
      />
      <ManualTimeEntryModal
        isOpen={manualEntryModal?.isOpen}
        onClose={() => setManualEntryModal({ isOpen: false, loading: false })}
        onSave={handleManualEntryCreate}
        employees={employees}
        loading={manualEntryModal?.loading}
      />
    </div>
  );
};

export default TimeEntryCorrections;